import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, filter, merge, of, take } from 'rxjs';
import { SettingsForm } from './interfaces';
import { TimedMarkerMapMenuFormService } from './timed-marker-map-menu-form.service';
import { timedMaintainerData, timedMarkerData } from './constants';
import { PipesModule } from '@shared/pipes/pipes.module';
import {
  DvButtonComponent,
  DvMenuComponent,
  DvMenuTriggerDirective,
} from '@devinco/ui/dds';
import { MapToolbarSettingsService } from '@features/map/services/map-toolbar-settings.service';
import { MapSetting } from '@features/map/constants/constants';

@Component({
  selector: 'app-timed-marker-map-menu-panel',
  templateUrl: './timed-marker-map-menu-panel.component.html',
  styleUrls: ['./timed-marker-map-menu-panel.component.scss'],
  imports: [
    CommonModule,
    PipesModule,
    DvDynamicFormModule,
    DvButtonComponent,
    TranslateModule,
    DvMenuComponent,
    DvMenuTriggerDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TimedMarkerMapMenuFormService],
})
export class TimedMarkerMapMenuPanelComponent implements OnInit, OnDestroy {
  private formService = inject(TimedMarkerMapMenuFormService);
  private mapSettingsService = inject(MapToolbarSettingsService);
  private destroyRef = inject(DestroyRef);

  /** toggles settings menu */
  showSettings = input<boolean>(true);
  /** disables all input fields and buttons on the menu */
  disableMenu = input<boolean>(false);
  /** toggles initial value of marker group settings menu item */
  markerGroupEnabled = input<boolean>(true);
  /** toggles visibility of search input field on the map */
  showAddressSearch = input<boolean>(false);
  /** toggles visibility of group markers checkbox selection in settings menu */
  hideGroupMarkers = input<boolean>(false);
  /** toggles visibility of a legend of locations status (avtive/idle/off) */
  showLocationsLegend = input<boolean>(false);
  /** shows car icons instead of color legend when set to true */
  showCarIcons = input<boolean>(false);
  slideToggleLabel = input<string>();
  showMaintainersLegend = input<boolean>(false);
  /** toggles visibility of a legend of geofence types (temporary/permanent) */
  showGeofenceLegend = input<boolean>(false);

  /** used to customize behavior and input fields of the common timed map marker menu component.
   * Both params (customForm/customFormElements) have to be defined in order to work. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() customForm?: FormGroup<FormOf<any>>;
  @Input() customFormElements?: DvDynamicFormElement[];
  @Input() mapSetting: MapSetting;

  formGroup: FormGroup<FormOf<SettingsForm>>;
  mainSettingsElements: DvDynamicFormElement[];
  searchElement: DvDynamicFormElement[];
  slideToggleElement: DvDynamicFormElement[];
  clusteringFormElements: DvDynamicFormElement[];
  readonly timedMarkerData = timedMarkerData;
  readonly timedMaintainerData = timedMaintainerData;

  autoRefreshClicked = output<boolean>();
  autoZoomFitClicked = output<boolean>();
  groupMarkersClicked = output<boolean>();
  searchAddressChanged = output<string | undefined>();
  slideToggleClicked = output<boolean>();
  customFormChanged = output<object>();

  constructor() {
    effect(() => {
      const disableMenu = this.disableMenu();

      if (!this.formGroup) return;

      if (disableMenu) {
        this.formGroup.disable();
      } else {
        this.formGroup.enable();
      }
    });
  }

  ngOnInit(): void {
    this.setFormElements();
    this.setupForm();
    this.listenToFormValueChanges();
    this.setMapSettings();
    this.setupMapSettingsSave();
  }

  ngOnDestroy(): void {
    this.saveMapSettings();
    this.formGroup.controls.searchAddress?.setValue(undefined);
  }

  private listenToFormValueChanges(): void {
    this.formGroup.controls.autoRefresh.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.autoRefreshClicked.emit(value);
      });

    this.formGroup.controls.autoZoomFit.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.autoZoomFitClicked.emit(value);
      });

    this.formGroup.controls.groupMarkers.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.groupMarkersClicked.emit(value);
      });

    this.formGroup.controls.searchAddress?.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.searchAddressChanged.emit(value);
      });

    this.formGroup.controls.slideToggle?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.slideToggleClicked.emit(!!value);
      });

    this.customForm?.valueChanges
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.customFormChanged.emit(value);
      });
  }

  private setFormElements(): void {
    this.mainSettingsElements = this.formService.mainSettingsElements;
    this.searchElement = this.formService.searchElement;
    this.slideToggleElement = this.formService.slideToggleElement(
      this.slideToggleLabel() || ''
    );
    this.clusteringFormElements = this.formService.clusteringFormElements;
  }

  private setMapSettings(): void {
    this.mapSettingsService
      .getMapSettingsBySettingName(this.mapSetting)
      .pipe(take(1))
      .subscribe(settings => {
        if (settings) {
          this.formGroup.patchValue(settings);
          this.customForm?.patchValue(settings);
        } else {
          this.formGroup.controls.groupMarkers.setValue(
            this.markerGroupEnabled(),
            {
              emitEvent: false,
            }
          );
          this.customFormElements?.forEach(el => {
            if (el.options.extra?.defaultValue) {
              this.customForm
                ?.get(el.options.key!)
                ?.setValue(el.options.extra.defaultValue);
            }
          });
        }
      });
  }

  private setupForm(): void {
    this.formGroup = DvFormUtils.createFormGroup([
      ...this.mainSettingsElements,
      ...this.searchElement,
      ...this.slideToggleElement,
      ...this.clusteringFormElements,
    ]);
  }

  private setupMapSettingsSave(): void {
    merge(this.customForm?.valueChanges || of(), this.formGroup.valueChanges)
      .pipe(
        debounceTime(10000),
        filter(() => !!this.customForm?.dirty || !!this.formGroup.dirty),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(_ => {
        this.saveMapSettings();
      });
  }

  private saveMapSettings(): void {
    this.mapSettingsService.setMapSettingsBySettingName(this.mapSetting, {
      ...this.customForm?.value,
      ...this.formGroup.value,
    });
  }
}
